import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../stores/languageStore';
import { useAuthStore } from '../../stores/authStore';
import { Lawsuit } from '@tvia-ai/shared';
import LoadingSpinner from '../ui/LoadingSpinner';
import { FILE_UPLOAD_CONFIG, validateFileList, formatFileSize } from '../../utils/fileUpload';
import { appendFileToFormData } from '../../utils/fileEncoding';
import axios from 'axios';

interface AIAnalysisStepProps {
  data: Lawsuit;
  onUpdate: (data: Partial<Lawsuit>) => void;
  onNext: () => void;
  onBack: () => void;
  onAnalysisComplete: (analysis: any, lawsuitId: string) => void;
}

// Extend the shared AIAnalysis interface with additional local properties
interface LocalAIAnalysis extends Lawsuit {
  // Additional local properties for UI
  viabilityAssessment?: string;
  estimatedCosts?: {
    filingFees: number;
    attorneyFees: number;
    otherCosts: number;
    total: number;
  };
  timeline?: string;
  successProbability?: number;
  risks?: string[];
  recommendations?: string[];
  requiredDocuments?: string[];
  similarCases: Array<{
    title: string;
    jurisdiction: string;
    outcome: string;
    settlementAmount: number;
    keyFactors: string;
    relevance: string;
  }>;
  followUpQuestions?: any[]; // Optional since analysis won't generate them, but needed for existing questions
  analysisStatus?: string; // Status of the analysis process
}

// Module-level Set to track which lawsuit IDs have already triggered the API call
const generatedQuestionsForLawsuit = new Set<string>();

const AIAnalysisStep: React.FC<AIAnalysisStepProps> = ({
  data,
  onNext,
  onAnalysisComplete,
}) => {
  const { getAuthToken } = useAuthStore();
  const { isRTL, language } = useLanguageStore();
  const { t } = useTranslation();
  
  // All hooks must be called at the top level, before any conditional returns
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<LocalAIAnalysis | null>(null);
  // const [showResults, setShowResults] = useState(false); // Removed unused
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [followUpAnswers, setFollowUpAnswers] = useState<Record<string, any>>({});
  // const [lawsuitId, setLawsuitId] = useState<string>(''); // Removed unused
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const lastAnalysisLawsuitId = useRef<string | null>(null);

  // Generate follow-up questions
  const generateQuestionsMutation = useMutation({
    mutationFn: async ({ lawsuitId, language }: { lawsuitId: string; language: string }) => {
      const response = await axios.post('/api/ai/generate-initial-questions', { lawsuitId, language }, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      return response.data;
    },
  });

  // Get AI analysis
  // const analysisMutation = useMutation({ // Removed unused
  //   mutationFn: async ({ lawsuitId, language }: { lawsuitId: string; language: string }) => {
  //     const response = await axios.post('/api/ai/analyze', { lawsuitId, language }, {
  //       headers: {
  //         'Authorization': `Bearer ${getAuthToken()}`,
  //       },
  //     });
  //     return response.data;
  //   },
  // });

  // Set local analysis state from props if available
  useEffect(() => {
    if (data.aiAnalysis && !analysis) {
      console.log('[AIAnalysisStep] Setting local analysis from data.aiAnalysis', data.aiAnalysis);
      setAnalysis(data.aiAnalysis as any);
    }
  }, [data.aiAnalysis]);

  useEffect(() => {
    console.log('[AIAnalysisStep] useEffect fired', {
      dataId: data.id,
      analysis,
      lastAnalysisLawsuitId: lastAnalysisLawsuitId.current
    });
    if (
      data.id &&
      !generatedQuestionsForLawsuit.has(data.id) &&
      !data.aiAnalysis
    ) {
      console.log('[AIAnalysisStep] Calling handleInitialAnalysis for lawsuitId:', data.id);
      generatedQuestionsForLawsuit.add(data.id);
      handleInitialAnalysis();
    }
  }, [data.id, data.aiAnalysis]);

  // Helper function to ensure analysis has all required properties
  const enrichAnalysis = (baseAnalysis: any): LocalAIAnalysis => {
    return {
      ...baseAnalysis,
      // Add local UI properties if not present
      viabilityAssessment: baseAnalysis.viabilityAssessment || 'Analysis pending completion of follow-up questions.',
      timeline: baseAnalysis.timeline || baseAnalysis.estimatedTimeline || 'Estimated 6-12 months for resolution.',
      successProbability: baseAnalysis.successProbability || Math.round((baseAnalysis.confidence || 0.7) * 100),
      risks: baseAnalysis.risks || ['Case outcome depends on evidence quality', 'Settlement negotiations may be required'],
      recommendations: baseAnalysis.recommendations || ['Gather all available documentation', 'Consider witness statements'],
      requiredDocuments: baseAnalysis.requiredDocuments || ['Medical records', 'Police report', 'Insurance correspondence'],
      similarCases: baseAnalysis.similarCases || [],

    };
  };

  const handleInitialAnalysis = async () => {
    console.log('[AIAnalysisStep] handleInitialAnalysis called', { lawsuitId: data.id });
    if (!data.id) {
      setLoading(false);
      alert('No lawsuit ID found. Please start from the beginning.');
      return;
    }
    try {
      setLoading(true);
      console.log('[AIAnalysisStep] Sending API request to /api/ai/generate-initial-questions', { lawsuitId: data.id, language });
      // Only generate questions for an existing lawsuit
      const questionsResponse = await generateQuestionsMutation.mutateAsync({
        lawsuitId: data.id,
        language,
      });
      console.log('[AIAnalysisStep] API response from /api/ai/generate-initial-questions', questionsResponse);
      if (questionsResponse.data && questionsResponse.data.questions) {
        const enrichedAnalysis = enrichAnalysis({
          followUpQuestions: questionsResponse.data.questions,
          analysisStatus: 'questions_ready'
        });
        setAnalysis(enrichedAnalysis);
        // Notify parent so it can update formData.aiAnalysis
        if (typeof onAnalysisComplete === 'function') {
          console.log('[AIAnalysisStep] Calling onAnalysisComplete with enrichedAnalysis and lawsuitId', { enrichedAnalysis, lawsuitId: data.id });
          onAnalysisComplete(enrichedAnalysis, data.id);
        }
      } 
    } catch (error: any) {
    
        // If the error code is LAWSUIT_ALREADY_PROCESSED, do not show an error
        if (error.status === 400 && error.response && error.response.data && error.response.data.code === 'LAWSUIT_ALREADY_PROCESSED' ) {
          console.info(error.response.data.message);
          return;
        }
        console.error('[AIAnalysisStep] Failed to generate questions. API response:', error.toString());
        
        setAnalysis(null);
        alert('Failed to generate follow-up questions.');
      
    }
    finally {
      setLoading(false);
    }
  };

  const handleFollowUpAnswer = (questionId: string, answer: any) => {
    setFollowUpAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  // Helper functions for text and file handling in file upload questions
  const getTextResponse = (questionId: string): string => {
    const answer = followUpAnswers[questionId];
    if (answer && typeof answer === 'object' && 'text' in answer) {
      return answer.text || '';
    }
    return '';
  };

  const getFilesResponse = (questionId: string): any[] => {
    const answer = followUpAnswers[questionId];
    if (answer && typeof answer === 'object' && 'files' in answer) {
      return answer.files || [];
    }
    if (Array.isArray(answer)) {
      return answer;
    }
    return [];
  };

  const handleTextResponseChange = (questionId: string, text: string) => {
    const updatedAnswer = {
      text,
      files: getFilesResponse(questionId)
    };
    setFollowUpAnswers(prev => ({ ...prev, [questionId]: updatedAnswer }));
  };

  const updateFilesResponse = (questionId: string, files: any[]) => {
    const updatedAnswer = {
      text: getTextResponse(questionId),
      files
    };
    setFollowUpAnswers(prev => ({ ...prev, [questionId]: updatedAnswer }));
  };

  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = followUpAnswers[questionId];
    
    if (!answer) return false;
    
    // For file upload questions with new structure
    if (answer && typeof answer === 'object' && ('text' in answer || 'files' in answer)) {
      const hasText = answer.text && answer.text.trim() !== '';
      const hasFiles = answer.files && answer.files.length > 0;
      return hasText || hasFiles;
    }
    
    // For legacy array format (files)
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    
    // For text questions
    if (typeof answer === 'string') {
      return answer.trim() !== '';
    }
    
    // For other question types
    return !!answer;
  };

  const handleFileUpload = (questionId: string, files: File[]) => {
    const currentFileArray = getFilesResponse(questionId);
    
    // Validate files using shared utility
    const validationError = validateFileList(files, currentFileArray.length);
    if (validationError) {
      setFileErrors(prev => ({ ...prev, [questionId]: validationError.message }));
      return;
    }

    // Clear any previous errors for this question
    setFileErrors(prev => ({ ...prev, [questionId]: '' }));
    
    // Create file objects with descriptions if the question allows it
    const currentQuestion = analysis?.followUpQuestions?.find(q => q.id === questionId);
    const allowDescription = currentQuestion?.allowDescription;
    
    if (allowDescription) {
      // Create file objects with empty descriptions
      const fileObjects = files.map(file => ({
        file,
        description: ''
      }));
      updateFilesResponse(questionId, [...currentFileArray, ...fileObjects]);
    } else {
      // Keep as simple file array
      updateFilesResponse(questionId, [...currentFileArray, ...files]);
    }
  };

  const handleFileDescriptionChange = (questionId: string, fileIndex: number, description: string) => {
    const currentFiles = getFilesResponse(questionId);
    if (currentFiles.length > fileIndex) {
      const updatedFiles = [...currentFiles];
      if (typeof updatedFiles[fileIndex] === 'object' && 'file' in updatedFiles[fileIndex]) {
        (updatedFiles[fileIndex] as any).description = description;
      }
      updateFilesResponse(questionId, updatedFiles);
    }
  };

  const removeFile = (questionId: string, fileIndex: number) => {
    const currentFiles = getFilesResponse(questionId);
    const updatedFiles = currentFiles.filter((_, i) => i !== fileIndex);
    updateFilesResponse(questionId, updatedFiles);
  };

  const handleNextQuestion = () => {
    if (analysis && analysis.followUpQuestions && currentQuestionIndex < analysis.followUpQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleAnalysisSubmission();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Add a new function to handle analysis submission with answers and files
  const handleAnalysisSubmission = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('lawsuitId', data.id);
      formData.append('language', language);
      // Build answers with file name mapping
      const answersWithFiles: Record<string, any> = {};
      Object.entries(followUpAnswers).forEach(([questionId, answer]) => {
        // Check if this is a file upload question with the new structure
        if (answer && typeof answer === 'object' && ('text' in answer || 'files' in answer)) {
          const fileNames: string[] = [];
          const files = answer.files || [];
          files.forEach((fileObjOrFile: any) => {
            if (fileObjOrFile && fileObjOrFile.file instanceof File) {
              fileNames.push(fileObjOrFile.file.name);
            } else if (fileObjOrFile instanceof File) {
              fileNames.push(fileObjOrFile.name);
            }
          });
          answersWithFiles[questionId] = {
            answer: answer.text || '',
            files: fileNames
          };
        } else if (Array.isArray(answer)) {
          // Legacy format - array of files or file objects
          const fileNames: string[] = [];
          answer.forEach((fileObjOrFile: any) => {
            if (fileObjOrFile && fileObjOrFile.file instanceof File) {
              fileNames.push(fileObjOrFile.file.name);
            } else if (fileObjOrFile instanceof File) {
              fileNames.push(fileObjOrFile.name);
            }
          });
          answersWithFiles[questionId] = {
            answer: '',
            files: fileNames
          };
        } else {
          answersWithFiles[questionId] = { answer };
        }
      });
      formData.append('answers', JSON.stringify(answersWithFiles));
      // Attach files for each question
      Object.entries(followUpAnswers).forEach(([, answer]) => {
        let filesToProcess: any[] = [];
        
        if (answer && typeof answer === 'object' && 'files' in answer) {
          filesToProcess = answer.files || [];
        } else if (Array.isArray(answer)) {
          filesToProcess = answer;
        }
        
        filesToProcess.forEach((fileObjOrFile: any) => {
          if (fileObjOrFile && fileObjOrFile.file instanceof File) {
            appendFileToFormData(formData, 'files', fileObjOrFile.file);
          } else if (fileObjOrFile instanceof File) {
            appendFileToFormData(formData, 'files', fileObjOrFile);
          }
        });
      });
      const response = await axios.post('/api/ai/analyze', formData, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data && response.data.data && response.data.data.analysis) {
        setAnalysis(enrichAnalysis(response.data.data.analysis));
        if (typeof onAnalysisComplete === 'function') {
          console.log('[AIAnalysisStep] handleAnalysisSubmission: Calling onAnalysisComplete and onNext', { analysis: response.data.data.analysis, lawsuitId: data.id });
          onAnalysisComplete(response.data.data.analysis, data.id);
        }
        if (typeof onNext === 'function') {
          onNext();
        }
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      let errorMsg = '';
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object' && 'data' in error.response) {
          errorMsg = error.response.data?.error || error.message;
        } else {
          errorMsg = error.message || String(error);
        }
      } else {
        errorMsg = String(error);
      }
      alert('Error during analysis: ' + errorMsg);
    }
  };

  // Auto-advance if there are no follow-up questions
  useEffect(() => {
    if (
      analysis &&
      (!analysis.followUpQuestions || analysis.followUpQuestions.length === 0)
    ) {
      onNext();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [analysis, onNext]);

  // Helper to determine if we are in the initial question phase
  const isInitialQuestionPhase = analysis && analysis.analysisStatus === 'questions_ready';

  // Now, after all hooks, do conditional returns for loading, error, etc.
  if (loading) {
    return (
      <div className="text-center py-12">
        <LoadingSpinner />
        <p className="mt-4 text-lg text-gray-600">
          {/* showResults ? t('filing.aiAnalysis.analyzing') : t('filing.aiAnalysis.generatingQuestions', 'Generating follow-up questions...') */}
          {t('filing.aiAnalysis.analyzing')}
        </p>
        <p className="mt-2 text-sm text-gray-500">
          {/* showResults ? t('filing.aiAnalysis.analyzingHint') : t('filing.aiAnalysis.generatingQuestionsHint', 'AI is analyzing your case to generate relevant questions...') */}
          {t('filing.aiAnalysis.analyzingHint')}
        </p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <LoadingSpinner />
        <p className="mt-4 text-lg text-gray-600">
          {t('filing.aiAnalysis.analyzing')}
        </p>
        <p className="mt-2 text-sm text-gray-500">
          {t('filing.aiAnalysis.analyzingHint')}
        </p>
      </div>
    );
  }

  // If there are no follow-up questions and we haven't shown results yet, show an error
  if (analysis && (!analysis.followUpQuestions || analysis.followUpQuestions.length === 0)) {
    console.warn('[AIAnalysisStep] No follow-up questions were generated. analysis:', analysis);
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600">
          {t('filing.aiAnalysis.noQuestionsError', 'No follow-up questions were generated. Please try again.')}
        </p>
        <button
          onClick={handleInitialAnalysis}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // Show follow-up questions
  if (analysis.followUpQuestions && analysis.followUpQuestions.length > 0) {
    const currentQuestion = analysis.followUpQuestions[currentQuestionIndex];
    
    return (
      <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('filing.aiAnalysis.followUpTitle')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('filing.aiAnalysis.followUpSubtitle')}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{t('filing.aiAnalysis.question')} {currentQuestionIndex + 1} {t('common.of')} {analysis.followUpQuestions?.length || 0}</span>
            <span>{Math.round(((currentQuestionIndex + 1) / (analysis.followUpQuestions?.length || 1)) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / (analysis.followUpQuestions?.length || 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-blue-50 p-6 rounded-lg">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex-1">
              {currentQuestion.question}
            </h3>
            <div className="ml-4 flex-shrink-0">
              {currentQuestion.required ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {isInitialQuestionPhase
                    ? t('common.recommended', 'Recommended')
                    : t('common.required')}
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {t('common.optional')}
                </span>
              )}
            </div>
          </div>

          {/* Legal Purpose Info */}
          {currentQuestion.legalPurpose && (
            <div className="mb-4 p-3 bg-blue-100 rounded-md">
              <p className="text-sm text-blue-800">
                <span className="font-medium">{t('filing.aiAnalysis.legalPurpose')}: </span>
                {currentQuestion.legalPurpose}
              </p>
            </div>
          )}

          {currentQuestion.type === 'text' && (
            <textarea
              value={followUpAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder={t('filing.aiAnalysis.answerPlaceholder')}
            />
          )}

          {(currentQuestion.type === 'select' || currentQuestion.type === 'multiple_choice') && (
            <select
              value={followUpAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.selectOption', 'Select an option...')}</option>
              {currentQuestion.options?.map((option: string, index: number) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}

          {currentQuestion.type === 'date' && (
            <input
              type="date"
              value={followUpAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}

          {currentQuestion.type === 'number' && (
            <input
              type="number"
              value={followUpAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('filing.aiAnalysis.enterNumber', 'Enter a number...')}
            />
          )}

          {currentQuestion.type === 'boolean' && (
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={currentQuestion.id}
                  value="yes"
                  checked={followUpAnswers[currentQuestion.id] === 'yes'}
                  onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-gray-700">{t('common.yes')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={currentQuestion.id}
                  value="no"
                  checked={followUpAnswers[currentQuestion.id] === 'no'}
                  onChange={(e) => handleFollowUpAnswer(currentQuestion.id, e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-gray-700">{t('common.no')}</span>
              </label>
            </div>
          )}

          {(currentQuestion.type === 'file_upload' || currentQuestion.type === 'document') && (
            <div className="space-y-4">
              {/* File upload section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('filing.aiAnalysis.fileUploadLabel', 'File Upload (Optional)')}
                </label>
                <div className="border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-6 transition-colors">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="mt-4">
                      <label htmlFor={`file-upload-${currentQuestion.id}`} className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          {t('filing.aiAnalysis.uploadFiles', 'Upload your files')}
                        </span>
                        <span className="mt-1 block text-sm text-gray-500">
                          {t('filing.aiAnalysis.dragAndDrop', 'or drag and drop')}
                        </span>
                      </label>
                      <input
                        id={`file-upload-${currentQuestion.id}`}
                        type="file"
                        multiple
                        accept={FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(',')}
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          handleFileUpload(currentQuestion.id, files);
                        }}
                        className="sr-only"
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {t('filing.aiAnalysis.fileTypes', `PDF, Word, Excel, images, or text files up to ${FILE_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB each (max ${FILE_UPLOAD_CONFIG.MAX_FILES_PER_UPLOAD} files)`)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Text input section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('filing.aiAnalysis.textResponse', 'Text Response (Optional)')}
                </label>
                <textarea
                  value={getTextResponse(currentQuestion.id)}
                  onChange={(e) => handleTextResponseChange(currentQuestion.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder={t('filing.aiAnalysis.textPlaceholder', 'Provide any additional information or context...')}
                />
              </div>
              
              {/* Show file validation errors */}
              {fileErrors[currentQuestion.id] && (
                <p className="text-sm text-red-600 mt-2">{fileErrors[currentQuestion.id]}</p>
              )}
              
              {/* Show uploaded files */}
              {getFilesResponse(currentQuestion.id).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t('filing.aiAnalysis.uploadedFiles', 'Uploaded Files:')}
                  </h4>
                  {getFilesResponse(currentQuestion.id).map((item, index) => {
                    const file = typeof item === 'object' && 'file' in item ? item.file : item;
                    const description = typeof item === 'object' && 'description' in item ? item.description : '';
                    const allowDescription = currentQuestion?.allowDescription;
                    
                    return (
                      <div key={index} className="bg-gray-50 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({formatFileSize(file.size)})
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(currentQuestion.id, index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        {allowDescription && (
                          <input
                            type="text"
                            value={description}
                            onChange={(e) => handleFileDescriptionChange(currentQuestion.id, index, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder={t('filing.aiAnalysis.fileDescriptionPlaceholder', 'Add a description for this file (optional)')}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={currentQuestionIndex === 0 ? onNext : handlePreviousQuestion}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            {t('common.back')}
          </button>

          <button
            onClick={handleNextQuestion}
            // Always enabled, regardless of required/answered
            className={`px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700`}
          >
            {currentQuestion.required && !isQuestionAnswered(currentQuestion.id)
              ? t('common.skip', 'Skip')
              : (
                currentQuestionIndex === (analysis.followUpQuestions?.length || 1) - 1
                  ? t('common.finish')
                  : t('common.next')
              )
            }
          </button>
        </div>
      </div>
    );
  }

  // If showResults is true but not handled by the above, return null or a placeholder
  return (
    <div className="text-center py-12">
      <LoadingSpinner />
      <p className="mt-4 text-lg text-gray-600">
        {t('filing.aiAnalysis.analyzing')}
      </p>
      <p className="mt-2 text-sm text-gray-500">
        {t('filing.aiAnalysis.analyzingHint')}
      </p>
    </div>
  );
};

export default AIAnalysisStep; 